import { useState, useEffect } from 'react';
import { Grid, Menu } from 'antd';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useNavigate } from 'react-router';
import { IconSvg } from 'shared/icon';
import { MenusItem } from 'router/types';
import type { MenuProps, GetProp } from 'antd';
import { StyledMenuItem } from './Layout.styled';

const { useBreakpoint } = Grid;

export interface SiderMenuProps{
  menus?: MenusItem[],
  mode?: string,
  page?: string,
  collapsed?: boolean,
  activeMenu: number,
  initialSubMenu: boolean,
  setInitialSubMenu: (param: boolean) => void,
  setCollapsed: (param: boolean) => void,
}

type MenuItemAntd = GetProp<MenuProps, 'items'>[number];
// todo dear front , please review collapsed part,when collapsed= true  sometimes besides selected menuItem previous opened menuItem icon stayed selected.
const prefix = '_';
const iconSizes = [
  { width: '1.29rem', height: '1.29rem', color: '#999999' },
  { width: '1.71rem', height: '1.71rem', color: '#999999' },
];
const SiderMenu = ({
  menus = [], mode = 'inline', page = '', activeMenu, collapsed = false, initialSubMenu, setInitialSubMenu, setCollapsed,
}:SiderMenuProps) => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const isMobile = !screens.md;
  const { t } = useTranslation();

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [active, setActive] = useState<number>();
  // local collapse state to catch collapse changing
  const [prevCollapsed, setPrevCollapsed] = useState(collapsed);

  const generateKey = (id: number): string => prefix + id;

  useEffect(() => {
    // when collapse state has updated or initial load - open keys
    if (prevCollapsed !== collapsed || initialSubMenu) {
      const openedMenu = _.find(menus, _.flow(_.property('children'), _.partialRight(_.some, { id: activeMenu })));
      setOpenKeys((prev) => (collapsed ? [] : _.union(prev, openedMenu ? [generateKey(openedMenu.id)] : [])));
      setActive(activeMenu);
      setPrevCollapsed(collapsed);
    }
  }, [active, activeMenu, menus, collapsed, prevCollapsed, initialSubMenu]);

  const handleOpenChange = (keys: string[]) => {
    setInitialSubMenu(false);
    setOpenKeys(collapsed && keys.length ? [keys[keys.length - 1]] : keys);
  };

  const handleOpenPage = (e:MouseEvent, route: MenusItem) => {
    const { key, path } = route;
    const url = (page ? key : path) || '#';
    if (e.button === 1) {
      window.open(url, '_blank');
    } else if (!e.button) {
      navigate(url);
    }
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const generateMenus = (data: MenusItem[]): MenuItemAntd[] => data?.map((item) => {
    if (item.children) {
      return {
        key: generateKey(item.id),
        label: (
          <StyledMenuItem $collapsed={collapsed}>
            {item.icon && <IconSvg icon={item.icon} {...iconSizes[+!!page]} />}
            <span>{t(item.name as string)}</span>
          </StyledMenuItem>
        ),
        children: generateMenus(item.children),
      };
    }

    return {
      key: generateKey(item.id),
      onMouseDown: (e:MouseEvent) => handleOpenPage(e, item),
      label: (
        <StyledMenuItem $collapsed={collapsed} $isChild={item.menuParentId}>
          {item.icon && <IconSvg icon={item.icon} {...iconSizes[+!!page]} />}
          <span>{t(item.name as string)}</span>
        </StyledMenuItem>
      ),
    };
  });

  const menuProps = _.pickBy({
    mode,
    selectedKeys: activeMenu ? generateKey(activeMenu) : '',
    openKeys,
    onOpenChange: handleOpenChange,
    motion: {},
  });

  return <Menu {...menuProps} items={generateMenus(menus)} />;
};

export default SiderMenu;
